import {Injectable} from '@angular/core';
import {environment} from "../../../../environments/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {MessageErrorResolver} from "../../utilities/message-error-resolver";
import {ResponseDto, ResponseWithErrorDto} from "../../models/exceptions/response-dto";
import {IPage} from "../../models/pagination/page";
import {catchError, EMPTY, map, Observable, tap} from "rxjs";
import {ITeachingInstitutionDto} from "../../models/teaching-institution-dto";
import { LoadingService } from './loading.service';
import {ICompleteTeachingInstitutionDto} from "../../models/complete-teaching-institution-dto";
import {TeachingInstitutionPostForm} from "../../models/form/teaching-institution-form";
import { TeachingInstitutionPutForm } from '../../models/form/put/teaching-institution-put-form';
import { TeachingInstitutionFilter } from '@models/institution/teaching-institution-filter';
import { PageRequest } from '@models/pagination/page-request';
import { IPageable } from '@models/pagination/pageable';

@Injectable({
  providedIn: 'root'
})
export class TeachingInstitutionService {
  private url: string = environment.apiUrl;

  constructor(
    private httpClient: HttpClient,
    private messageErrorResolver: MessageErrorResolver,
    private loadingService: LoadingService
  ) {}

  public getAllTeachingInstitution(filters?: TeachingInstitutionFilter, pageRequest?: PageRequest): Observable<IPage<ITeachingInstitutionDto>> {

    let params = new HttpParams();
    if (pageRequest?.page) params = params.set("page", pageRequest.page);
    if (pageRequest?.size) params = params.set("size", pageRequest.size);
    if (filters?.insTxCodigoInep) params = params.set("insTxCodigoInep", filters.insTxCodigoInep);
    if (filters?.insTxCnpj) params = params.set("insTxCnpj", filters.insTxCnpj);
    if (filters?.insTxNome) params = params.set("insTxNome", filters.insTxNome);
    if (filters?.insTxNomeFantasia) params = params.set("insTxNomeFantasia", filters.insTxNomeFantasia);
    if (filters?.insBlPrivada) params = params.set("insBlPrivada", filters.insBlPrivada.toString());
    if (filters?.insTxEsfera) params = params.set("insTxEsfera", filters.insTxEsfera);
    if (filters?.fedNrId) params = params.set("fedNrId", filters.fedNrId.toString());

    return this.httpClient.get<ResponseDto<IPage<ITeachingInstitutionDto>>>(
      `${this.url}/v1/instituicoes-ensino`, { params }
    )
    .pipe(
      map(response => response.response),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  getInstitutionById$(insNrId: number): Observable<ITeachingInstitutionDto> {
    this.loadingService.activate();
    return this.httpClient.get<ResponseDto<ITeachingInstitutionDto>>(this.url + `/v1/instituicoes-ensino/${insNrId}`)
      .pipe(
        map(response => response.response),
        catchError(err => {
          this.loadingService.deactivate();
          throw EMPTY;
        }),
        tap(() => this.loadingService.deactivate())
      );
  }

  getInstitutionByEvent$(filters: TeachingInstitutionFilter, eveNrId: number, pageRequest?: IPageable): Observable<IPage<ITeachingInstitutionDto>> {

    let params = new HttpParams();

    if (pageRequest?.page) params = params.set("page", pageRequest.page);
    if (pageRequest?.size) params = params.set("size", pageRequest.size);
    if (filters?.insTxCodigoInep) params = params.set("insTxCodigoInep", filters.insTxCodigoInep);
    if (filters?.insTxCnpj) params = params.set("insTxCnpj", filters.insTxCnpj);
    if (filters?.insTxNome) params = params.set("insTxNome", filters.insTxNome);
    if (filters?.insTxNomeFantasia) params = params.set("insTxNomeFantasia", filters.insTxNomeFantasia);
    if (filters?.insBlPrivada) params = params.set("insBlPrivada", filters.insBlPrivada.toString());
    if (filters?.insTxEsfera) params = params.set("insTxEsfera", filters.insTxEsfera);
    if (filters?.fedNrId) params = params.set("fedNrId", filters.fedNrId.toString());

    return this.httpClient.get<ResponseDto<IPage<ITeachingInstitutionDto>>>(
      `${this.url}/v1/instituicoes-ensino/evento/${eveNrId}`, { params }
    )
    .pipe(
      map(response => response.response),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  getCompleteInstitutionById(insNrId: number) {
    return this.httpClient.get<ResponseDto<ICompleteTeachingInstitutionDto>>(this.url + `/v1/instituicoes-ensino/${insNrId}/completo`)
      .pipe(
        map(response => response.response),
        catchError(err => {
          throw EMPTY;
        }),
      );
  }

  postInstitution(form: TeachingInstitutionPostForm): Observable<ITeachingInstitutionDto> {
    return this.httpClient.post<ResponseDto<ITeachingInstitutionDto>>(this.url + `/v1/instituicoes-ensino`, form)
      .pipe(
        map(response => response.response),
        catchError(err => {
          throw err;
        }),
      );
  }

  editTeachingInstitution(insNrId: number, form: TeachingInstitutionPutForm) {
    return this.httpClient.put<ResponseDto<ICompleteTeachingInstitutionDto>>(this.url + `/v1/instituicoes-ensino/${insNrId}`, form)
      .pipe(
        map(response => response.response),
        catchError(err => {
          throw err;
        }),
      );
  }

  deleteInsitution(insNrId: number): Observable<void> {
    return this.httpClient.delete<void>(this.url + `/v1/instituicoes-ensino/${insNrId}`)
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

}
